/** @jsxImportSource @emotion/react */
import * as React from "react";
import styled from "@emotion/styled";
import { css, Global } from "@emotion/react";
import { useLocation } from "react-router-dom";
import logoSVG from "assets/images/logo.svg";
import {
  Avatar,
  Button,
  Container,
  H1,
  Hr,
  IconBell,
  H3,
  IconChevronBold,
  IconClose,
  IconMenu,
  Link,
  NavLink,
  Text,
} from "atoms";
import { useActiveConnector, useAuth } from "features/auth";
import { NotificationContent, useNotifications } from "features/notifications";
import {
  HUGS,
  useProfilePoints,
  useProfileCompleteCheck,
} from "features/profile";
import { GlobalSearch } from "features/search";
import { featureFlags } from "config";
import { ProfileCompletePopup } from "./ProfileCompletePopup";

function SiteHeader() {
  const { user, signOut } = useAuth();
  const connector = useActiveConnector();

  // Profile
  const { data: profilePoints } = useProfilePoints();
  const { showProfileCompletePopup, ignoreProfileCompletePopup } =
    useProfileCompleteCheck();

  // Notifications
  const { data } = useNotifications();
  const unreadNotifications = data ? data.filter((d) => !d.readAt) : [];
  const unreadNotificationsList = unreadNotifications.slice(0, 4);
  const unreadCount = unreadNotifications.length;

  // create hug avatar src
  let avatarSrc;
  if (user?.avatarUrl) {
    avatarSrc = user.avatarUrl;
  } else {
    avatarSrc = `https://thehug.xyz/assets/tokens/${
      HUGS[user?.hugImageIds?.[0] ?? 0].image
    }`;
  }

  const location = useLocation();

  // Handle signout and wallet disconnect
  const handleSignOut = () => {
    connector?.disconnect();
    signOut();
  };

  // Mobile navigation visibility
  const [isNavShowing, setIsNavShowing] = React.useState(false);
  const toggleNavShowing = () => setIsNavShowing(!isNavShowing);

  const superAdmin = !!user?.roles.find((r) => r === "superAdmin");
  const admin = superAdmin || !!user?.roles.find((r) => r === "admin");
  const moderator = admin || !!user?.roles.find((r) => r === "curator-level-2");
  const isQA = admin || user?.roles?.find((u) => u === "qa");

  const showNotificationMenu = user && featureFlags.enableNotificationsMenu;

  // On navigation change, make sure mobile navigation is hidden
  React.useEffect(() => {
    if (isNavShowing) setIsNavShowing(false);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [location.pathname]);

  // Event listeners to remove focus from submenus after navigation
  const submenuBlur = (event: React.KeyboardEvent | React.MouseEvent) => {
    const el = event.target as HTMLElement;
    if (el.tagName === "A") {
      // Unfocus the anchor element
      el.blur();
      // Briefly hide the submenu so it isn't visible from mouse hovering over anchor after click
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      el.parentElement!.style.display = "none";
      setTimeout(() => {
        /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
        el.parentElement!.style.display = "block";
      }, 10);
    }
  };

  // Sets isNavShowing to false when mobile menu is pen and screen is resized above 830px
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 831) {
        setIsNavShowing(false);
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const globalCSS = {
    "html,body": {
      ...(isNavShowing &&
        window.innerWidth < 831 && {
          height: "100vh",
          width: "100vw",
          overflow: "hidden",
        }),
    },
  };

  return (
    <Header>
      <Global styles={globalCSS} />
      <SContainer>
        <Logo>
          <LogoLink to="/">
            <img src={logoSVG} alt="Hug Home" />
          </LogoLink>
        </Logo>

        <GlobalSearch />

        <NavToggle
          type="button"
          onClick={toggleNavShowing}
          aria-label={`${isNavShowing ? "Close" : "Open"} Menu`}
        >
          {isNavShowing ? <IconClose /> : <IconMenu />}
          {showNotificationMenu && !isNavShowing && unreadCount > 0 && (
            <NotificationCount>{unreadCount}</NotificationCount>
          )}
        </NavToggle>

        {showProfileCompletePopup && (
          <MobileProfileCompletePopup onCancel={ignoreProfileCompletePopup} />
        )}

        <Nav aria-label="Main navigation" isShowing={isNavShowing}>
          <Dropdown
            css={{
              alignSelf: "stretch",
              alignItems: "center",
              display: "inline-flex",
            }}
          >
            <DropdownToggle
              arrow={null}
              aria-haspopup="true"
              css={{ marginRight: 10 }}
            >
              Learn More
              <DropdownArrow className="DropdownArrow" aria-hidden="true" />
            </DropdownToggle>

            <DropdownMenu
              className="DropdownMenu"
              aria-label="submenu"
              onClick={submenuBlur}
            >
              <SubMenuLink to="/search/">Explore</SubMenuLink>
              <SubMenuLink to="/for-creators">For Creators</SubMenuLink>
              <SubMenuLink to="/dei-initiatives">DEI Initiatives</SubMenuLink>
              <SubMenuLink to="/faq">FAQ</SubMenuLink>
              <SubMenuLink to="/about">About Us</SubMenuLink>
            </DropdownMenu>
          </Dropdown>

          {user ? (
            <Dropdown disabled={showProfileCompletePopup}>
              <DropdownToggle
                arrow={null}
                aria-haspopup="true"
                padded={false}
                aria-label="My account"
                disabled={showProfileCompletePopup}
              >
                <Avatar alt="avatar" src={avatarSrc} aria-hidden="true" />
              </DropdownToggle>

              {showProfileCompletePopup && (
                <DesktopProfileCompletePopup
                  onCancel={ignoreProfileCompletePopup}
                />
              )}

              <DropdownMenu
                className="DropdownMenu"
                aria-label="submenu"
                onClick={submenuBlur}
              >
                <MenuProfileHeader>
                  <Avatar alt="avatar" src={avatarSrc} aria-hidden="true" />
                  <div>
                    <Link to="/profile">
                      <Text size="md">{user.name}</Text>
                    </Link>
                    {profilePoints && (
                      <Score>
                        {profilePoints?.pointsBalance.toLocaleString()}
                        &nbsp;HUG&nbsp;Points
                      </Score>
                    )}
                  </div>
                </MenuProfileHeader>

                {profilePoints && (
                  <>
                    <DesktopPoints>
                      <SScore>
                        {profilePoints?.pointsBalance.toLocaleString()}{" "}
                        HUG&nbsp;Points
                      </SScore>
                    </DesktopPoints>
                    <SHr />
                  </>
                )}

                <SubMenuLink to="/profile">My Profile</SubMenuLink>
                {showNotificationMenu && (
                  <MobileSubMenuLink to="/profile/notifications/">
                    Notifications
                    {unreadCount > 0 && (
                      <NotificationCount>{unreadCount}</NotificationCount>
                    )}
                  </MobileSubMenuLink>
                )}

                <SubMenuLink to="/profile/mobileSignIn">
                  Mobile Sign In
                </SubMenuLink>

                <SubMenuLink to="/" onClick={handleSignOut}>
                  Sign Out
                </SubMenuLink>
                {(admin || moderator) && <SHr />}
                {admin && <SubMenuLink to="/admin/users">Admin</SubMenuLink>}
                {isQA && (
                  <>
                    <SubMenuLink to="/qa/tickets">
                      Quality Assurance
                    </SubMenuLink>
                    <SubMenuLink to="/search?status=DRAFT">
                      Draft Projects/Artists
                    </SubMenuLink>
                  </>
                )}
                {moderator && (
                  <SubMenuLink to="/moderation">Moderation</SubMenuLink>
                )}
              </DropdownMenu>
            </Dropdown>
          ) : (
            <SignInButton to="/sign-in" variant="gradient" arrow="dr">
              Sign In
            </SignInButton>
          )}

          {showNotificationMenu && (
            <NotificationDropdown>
              <DropdownToggle
                arrow={null}
                aria-haspopup="true"
                aria-label={`${unreadCount} new notifications`}
              >
                <NotificationIconWrapper>
                  <IconBell aria-hidden="true" />
                  {unreadCount > 0 && (
                    <NotificationCount>{unreadCount}</NotificationCount>
                  )}
                </NotificationIconWrapper>
              </DropdownToggle>

              <NotificationDropdownMenu
                aria-label="submenu"
                onClick={submenuBlur}
              >
                <H3 as="p" size="xs" css={{ marginTop: 8 }}>
                  {unreadNotifications.length > 0
                    ? `${unreadNotifications.length} unread notification${
                        unreadNotifications.length > 1 ? "s" : ""
                      }`
                    : "Recent notifications"}
                </H3>

                {unreadNotifications.length > 0 ? (
                  <NotificationList>
                    {unreadNotificationsList.map((n) => (
                      <li key={n.notificationId}>
                        <NotificationContent isDismissible notification={n} />
                      </li>
                    ))}
                  </NotificationList>
                ) : (
                  <Text size="xs">You have no new notifications.</Text>
                )}

                <Link to="/profile/notifications" size="xs">
                  View all notifications
                </Link>
              </NotificationDropdownMenu>
            </NotificationDropdown>
          )}
        </Nav>
      </SContainer>
    </Header>
  );
}

const headerHeight = {
  small: css({
    height: 40,
    margin: "10px 1rem",
  }),
  large: css({
    height: 55,
    margin: "10px 2rem",
  }),
};

const SContainer = styled(Container)({
  display: "flex",
  gap: "1rem",
  alignItems: "center",
  padding: "0",
  "@media (min-width: 600px)": {
    padding: "0",
  },
});

const Header = styled.header(({ theme }) => ({
  background: theme.colors.primary,
  position: "sticky",
  top: 0,
  transition: "background 0.2s ease",
  width: "100%",
  zIndex: 500,
}));

const Logo = styled(H1)({ margin: 0 });
const LogoLink = styled(Link)(headerHeight.small, ({ theme }) => ({
  display: "block",
  lineHeight: 0,
  transition: "transform 0.15s ease",
  [theme.breakpoints.desktop]: headerHeight.large,
  marginRight: "10px !important",
  "&:hover": {
    transform: "scale(1.1) translate(2px, 2px)",
  },
  "&:active": {
    transform: "scale(1, 1.2) translate(2px, 2px)",
  },
  img: {
    height: "100%",
    [theme.breakpoints.desktop]: {
      padding: "5px 0",
    },
  },
}));

const Nav = styled.nav<{ isShowing: boolean }>(({ isShowing, theme }) => ({
  display: "none",
  [theme.breakpoints.mobile]: {
    background: theme.colors.primary,
    display: isShowing ? "flex" : "none",
    flexDirection: "column",
    position: "fixed",
    top: 60,
    right: 0,
    left: 0,
    padding: "1rem",
    textAlign: "center",
    height: "calc(100vh - 60px)",
    overflow: "auto",
  },
  [theme.breakpoints.desktop]: {
    alignItems: "center",
    background: "none",
    display: "flex",
    flex: "1",
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: "1em",
    height: 40,
    paddingRight: theme.spacing.xl,
    position: "static",
  },
}));

const NavToggle = styled.button(headerHeight.small, ({ theme }) => ({
  alignItems: "center",
  marginLeft: "auto",
  background: "none",
  border: 0,
  color: theme.colors.fg,
  cursor: "pointer",
  display: "flex",
  fontSize: theme.fontSizes.xl,
  position: "relative",
  transition: "transform 0.15s ease, color 0.15s ease",
  [theme.breakpoints.desktop]: {
    display: "none",
  },
  "&:hover": {
    transform: "translate(2px, 2px)",
    color: theme.colors.pink,
  },
  "&:active": {
    transform: "scale(0.9, 1.1)",
  },
  ".NotificationCount": {
    position: "absolute",
    top: "-2px",
    right: "-4px",
  },
}));
// eslint-disable-next-line
const SNavLink: any = styled(NavLink)(({ theme }) => ({
  background: "none",
  display: "block",
  textAlign: "left",
  position: "relative",
  ".DropdownArrow": {
    display: "inline",
    width: ".6rem",
    height: "auto",
  },
  "&:hover, &:focus": {
    background: "none",
    textDecoration: "none",
    color: theme.colors.primary,
    svg: {
      color: theme.colors.fg,
    },
  },
  [theme.breakpoints.mobile]: {
    margin: "0 0 1rem",
    padding: "0.5rem",
    "&:after": {
      background: theme.colors.fg,
      bottom: "-0.5rem",
      content: "''",
      display: "block",
      height: "1px",
      left: 0,
      position: "absolute",
      width: "100%",
    },
  },
  [theme.breakpoints.desktop]: {
    display: "inline",
    "&:hover": {
      textDecoration: "none",
      color: theme.colors.green,
    },
    svg: {
      display: "none",
    },
  },
}));

const SignInButton = styled(Link)(({ theme }) => ({
  justifyContent: "center",
  marginTop: "1rem",
  order: 99,
  "&:hover": {
    color: `${theme.colors.fg} !important`,
  },
  [theme.breakpoints.mobile]: {
    fontSize: theme.fontSizes.lg,
    paddingTop: "1em",
    paddingBottom: "1em",
    width: "100%",
  },
  [theme.breakpoints.desktop]: {
    fontSize: theme.fontSizes.sm,
    marginTop: "0",
  },
}));

const DropdownToggle = styled(SNavLink, {
  shouldForwardProp: (prop) => prop !== "padded",
})<{ padded?: boolean }>(({ padded = true, theme }) => ({
  ...theme.fonts.body,
  appearance: "none",
  border: 0,
  display: "none",
  fontWeight: "bold",
  textTransform: "none",
  fontSize: theme.fontSizes.sm,
  img: {
    height: 40,
    width: 40,
  },
  "&:hover, &:focus": {
    color: theme.colors.fg,
    transform: "none",
  },
  "&:disabled": {
    background: "unset",
    color: theme.colors.fg,
  },
  [theme.breakpoints.desktop]: {
    alignItems: "center",
    display: "flex",
    padding: 0,
    zIndex: 1,
    svg: {
      display: "inline-block",
      fontSize: "1.3rem",
    },
  },
})).withComponent(Button);

const DropdownArrow = styled(IconChevronBold)({
  display: "inline",
  width: ".6rem",
  marginLeft: 6,
  marginRight: 0,
  height: "auto",
});

const DropdownMenu = styled.div(({ theme }) => ({
  a: {
    marginBottom: 13,
  },
  [theme.breakpoints.mobile]: {
    backgroundColor: "white",
    borderRadius: theme.borderRadius.md,
    marginBottom: "1rem",
    padding: "1.5rem",
    width: "100%",
    "a:last-of-type": {
      marginBottom: 0,
    },
  },
  [theme.breakpoints.desktop]: {
    background: theme.colors.bgLight,
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.boxShadow.dark,
    opacity: 0,
    minWidth: "calc(150px + 3vw)",
    padding: `${theme.spacing.sm} calc(${theme.spacing.sm} + 6px)`,
    position: "absolute",
    right: -3,
    top: "100%",
    transition: "opacity ease-in 0.2s",
    visibility: "hidden",
    "a:last-of-type": {
      marginBottom: 6,
    },
  },
}));
DropdownMenu.defaultProps = { className: "DropdownMenu" };

const SubMenuLink = styled(SNavLink)(({ theme }) => ({
  ...theme.fonts.body,
  display: "flex",
  fontSize: theme.fontSizes.md,
  fontWeight: "bold",
  gap: 10,
  textTransform: "none",
  "&:hover,&:focus": {
    color: theme.colors.primary,
  },
  "&::after": {
    content: "none",
  },
  [theme.breakpoints.mobile]: {
    margin: 0,
    padding: 0,
  },
  [theme.breakpoints.desktop]: {
    fontSize: theme.fontSizes.xs,
    display: "block",
    margin: `${theme.spacing.sm} 0`,
  },
}));

const MobileSubMenuLink = styled(SubMenuLink)({
  "@media (min-width: 831px)": {
    display: "none",
  },
});

const Dropdown = styled.div<{ disabled?: boolean }>(({ disabled, theme }) => ({
  position: "relative",
  textAlign: "left",
  ...(!disabled && {
    "&:focus-within,&:hover": {
      ".DropdownMenu": {
        visibility: "visible",
        opacity: 1,
        zIndex: 99,
      },
    },
  }),
}));

const Score = styled(Text)(({ theme }) => ({
  ...theme.fonts.body,
  fontWeight: "bold",
  width: "min-content",
  color: theme.colors.fg,
  lineHeight: "1",
  margin: "0 0 0 8px",
  fontSize: 14,
  textTransform: "none",
  span: {
    fontSize: 12,
  },
}));
const SScore = styled(Score)({
  margin: 0,
  width: "auto",
});

const SHr = styled(Hr)(({ theme }) => ({
  height: 1,
  margin: "1.25em 0",
  opacity: ".25",
  [theme.breakpoints.desktop]: {
    margin: ".8em 0",
  },
}));

const MenuProfileHeader = styled.div(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: "0 10px",
  position: "relative",
  width: "max-content",
  [theme.breakpoints.desktop]: {
    display: "none",
  },
  img: {
    flex: "0 0 60px",
    height: 60,
    width: 60,
  },
  "a::after": {
    content: "''",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  p: {
    fontWeight: "bold",
    margin: 0,
  },
  span: {
    display: "inline-flex",
    flexWrap: "nowrap",
    fontWeight: "normal",
    margin: 0,
    width: "auto",
    whiteSpace: "nowrap",
  },
  hr: {
    flex: "0 0 100%",
  },
}));
const DesktopPoints = styled.div(({ theme }) => ({
  margin: ".5rem 0",
  [theme.breakpoints.mobile]: {
    display: "none",
  },
}));
const NotificationCount = styled.span(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.colors.yellow,
  border: "1px solid #EBCF42",
  borderRadius: theme.borderRadius.round,
  display: "flex",
  fontSize: 13,
  fontWeight: "normal",
  height: 18,
  justifyContent: "center",
  minWidth: 18,
  padding: "0 3px",
  maxWidth: "max-content",
  color: "black !important",
}));
NotificationCount.defaultProps = { className: "NotificationCount" };

const NotificationDropdown = styled(Dropdown)(({ theme }) => ({
  flex: "0 0 40px",
  position: "relative",
  ".DropdownMenu": {
    [theme.breakpoints.mobile]: {
      display: "none",
    },
  },
  a: {
    display: "inline-block",
    fontWeight: "bold",
    marginBottom: 5,
  },
}));

const NotificationDropdownMenu = styled(DropdownMenu)(({ theme }) => ({
  width: 400,
  maxWidth: "80vw",
  "ul p": {
    fontSize: theme.fontSizes.xxs,
  },
}));

const NotificationIconWrapper = styled.div(({ theme }) => ({
  alignItems: "center",
  alignSelf: "center",
  backgroundColor: "rgba(255,255,255, .2)",
  borderRadius: theme.borderRadius.round,
  display: "flex",
  justifyContent: "center",
  height: 40,
  padding: 0,
  width: 40,
  "&:hover svg": {
    stroke: "transparent",
  },
  ".NotificationCount": {
    position: "absolute",
    top: "-2px",
    right: "-4px",
  },
}));

const NotificationList = styled.ul(({ theme }) => ({
  listStyleType: "none",
  padding: 0,
  margin: "1em 0",
  fontSize: theme.fontSizes.xxs,
  lineHeight: 1.3,
  li: {
    borderBottom: `1px solid ${theme.colors.gray10}`,
    borderTop: `1px solid ${theme.colors.gray10}`,
    padding: "1em 0",
  },
  "li+li": {
    borderTop: "0",
  },
  "&>li::before": {
    content: `"\\200B"`,
    position: "absolute",
  },
  "li span": {
    display: "none",
  },
}));

const MobileProfileCompletePopup = styled(ProfileCompletePopup)(
  ({ theme }) => ({
    left: "auto",
    top: "calc(100% + 1rem)",
    right: "1rem",
    [theme.breakpoints.desktop]: {
      display: "none",
    },
  })
);
const DesktopProfileCompletePopup = styled(ProfileCompletePopup)(
  ({ theme }) => ({
    [theme.breakpoints.mobile]: {
      display: "none",
    },
  })
);

export { SiteHeader };
